import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

/** 6-box one-time-code input with auto-advance, backspace, and paste. */
export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  length?: number;
  disabled?: boolean;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const chars = value.split('');

  const setAt = (i: number, digit: string) => {
    const arr = Array.from({ length }, (_, k) => chars[k] ?? '');
    arr[i] = digit;
    onChange(arr.join('').slice(0, length));
  };

  const handleChange = (i: number, raw: string) => {
    const digit = raw.replace(/[^0-9]/g, '').slice(-1);
    if (!digit) return;
    setAt(i, digit);
    if (i < length - 1) refs.current[i + 1]?.focus();
  };

  const handleKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (chars[i]) {
        setAt(i, '');
      } else if (i > 0) {
        refs.current[i - 1]?.focus();
        setAt(i - 1, '');
      }
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, length);
    if (!digits) return;
    onChange(digits);
    refs.current[Math.min(digits.length, length - 1)]?.focus();
  };

  return (
    <div className="otp-row">
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className="otp-box"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={chars[i] ?? ''}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
}
